import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation } from "convex/react";
import { toast } from "sonner";

import { api } from "$convex/convexApi";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useContractsByStatus } from "@/hooks/useContracts";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { formatCurrency, formatDate, formatStatusLabel } from "@/lib/format";
import { hasPermission } from "@/lib/roles";
import { toUserFacingErrorMessage } from "@/lib/errors";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { getContractStatusBadgeClass } from "@/lib/status-tag-colors";
import { CreatePrepaidInventoryDialog } from "./CreatePrepaidInventoryDialog";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "signed", label: "Signed" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
] as const;

type ContractTab = (typeof STATUS_TABS)[number]["value"];

function useColumns() {
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "contractNumber",
        accessorKey: "contractNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Contract" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link to={`/sales/contracts/${row.original._id}`} className="font-mono text-xs underline-offset-2 hover:underline">
              {row.original.contractNumber || row.original._id?.slice(-6)}
            </Link>
            {row.original.isPrepaid ? (
              <Badge variant="secondary" className="text-[10px]">PREPAID</Badge>
            ) : null}
          </div>
        ),
        meta: { label: "Contract", placeholder: "Search contract...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "customerName",
        accessorKey: "customerName",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Customer" />,
        cell: ({ row }) => {
          const company = row.original.user?.company || "";
          const clientName = row.original.customerName || "";
          return (
            <div className="flex flex-col">
              <span className="text-xs font-medium">
                {company || clientName}
              </span>
              {company && company !== clientName && (
                <span className="text-xs text-muted-foreground">
                  {clientName}
                </span>
              )}
            </div>
          );
        },
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const search = String(filterValue).toLowerCase();
          const customerName = (row.original.customerName ?? "").toLowerCase();
          const company = (row.original.user?.company ?? "").toLowerCase();
          return customerName.includes(search) || company.includes(search);
        },
        meta: { label: "Customer", placeholder: "Search customer...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorFn: (row) => Boolean(row.user?.isEnterprise),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Enterprise" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getEnterpriseBadgeClass(row.original.user?.isEnterprise)}>
            {getEnterpriseBadgeLabel(row.original.user?.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select",
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) =>
          filterValues.some((value: string) => value === String(Boolean(row.original.user?.isEnterprise))),
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getContractStatusBadgeClass(row.original.status)}>
            {formatStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "select",
          options: STATUS_TABS.filter((t) => t.value !== "all").map((t) => ({ label: t.label, value: t.value })),
        },
        enableColumnFilter: true,
      },
      {
        id: "volumeCommittedBags",
        accessorKey: "volumeCommittedBags",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Bags" />,
        meta: { label: "Bags", variant: "number", placeholder: "Min bags" },
        enableColumnFilter: true,
      },
      {
        id: "totalValue",
        accessorKey: "totalValue",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Total $" />,
        cell: ({ row }) => {
          const val = row.original.totalValue;
          return val ? <span className="font-medium">{formatCurrency(val)}</span> : <span className="text-muted-foreground">-</span>;
        },
        meta: { label: "Total Value", variant: "number", placeholder: "Min value" },
        enableColumnFilter: true,
      },
      {
        id: "startDate",
        accessorKey: "startDate",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Start" />,
        cell: ({ row }) => <span className="text-sm">{row.original.startDate ? formatDate(row.original.startDate) : "-"}</span>,
        meta: { label: "Start Date", variant: "dateRange" },
        enableColumnFilter: true,
      },
      {
        id: "endDate",
        accessorKey: "endDate",
        header: ({ column }) => <DataTableColumnHeader column={column} label="End" />,
        cell: ({ row }) => <span className="text-sm">{row.original.endDate ? formatDate(row.original.endDate) : "-"}</span>,
        meta: { label: "End Date", variant: "dateRange" },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

// Map frontend display statuses to backend contractStatus values
const FRONTEND_TO_BACKEND_STATUS: Record<string, string> = {
  pending: "pending_approval",
};

function toBackendStatus(frontendStatus: string): string {
  return FRONTEND_TO_BACKEND_STATUS[frontendStatus] ?? frontendStatus;
}

export default function SalesContractsPage() {
  const columns = useColumns();
  const [activeTab, setActiveTab] = React.useState<ContractTab>("all");
  const contracts = useContractsByStatus(activeTab);
  const { isPending, userRole } = useAuthIdentity();
  const canCreateContracts = hasPermission(userRole, "contracts:approve");
  const isLoading = contracts === undefined || (isPending && contracts.length === 0);
  const bulkUpdateStatus = useMutation(api.contracts.bulkUpdateContractStatus);

  const handleBulkStatusChange = React.useCallback(
    async (
      selectedRows: any[],
      targetStatus: string,
      successLabel: string,
      clearSelection: () => void,
    ) => {
      const updates = selectedRows
        .map((row) => row._id ?? row.id)
        .filter(Boolean)
        .map((id) => ({
          contractId: id,
          status: targetStatus,
          notes: `Bulk ${successLabel}`,
        }));

      if (updates.length === 0) {
        toast.error("No contracts selected");
        return;
      }

      try {
        const result = await bulkUpdateStatus({ updates: updates as any[] });
        const data = result?.data ?? result;
        const failed = (data?.failed ?? []).length;
        const successful = (data?.successful ?? []).length;

        if (failed > 0) {
          toast.error(`${successful} updated, ${failed} failed`);
        } else {
          toast.success(`${successLabel}: ${successful} contract${successful === 1 ? "" : "s"}`);
        }
        clearSelection();
      } catch (error) {
        toast.error(toUserFacingErrorMessage(error, "Failed to update contracts"));
      }
    },
    [bulkUpdateStatus],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales Contracts</h1>
          <p className="text-muted-foreground mt-1">Contract pipeline and approval flow</p>
        </div>
        {canCreateContracts ? (
          <div className="flex items-center gap-2">
            <CreatePrepaidInventoryDialog />
            <Button asChild size="sm">
              <Link to="/sales/contracts/new">Create Contract</Link>
            </Button>
          </div>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contracts</CardTitle>
          <CardDescription>Filter by status to manage the contract pipeline.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ContractTab)} className="mb-4">
            <TabsList>
              {STATUS_TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {isLoading ? (
            <DataTableSkeleton columnCount={columns.length} rowCount={10} withPagination />
          ) : (
          <InternalEntityDataTable
            data={contracts.map((c: any) => ({ ...c, id: c._id || c.id }))}
            columns={columns}
            initialSorting={[{ id: "contractNumber", desc: true }]}
            enableSelection={canCreateContracts}
            actionBar={
              canCreateContracts
                ? ({ selectedRows, clearSelection }) => {
                    const statuses = new Set(
                      selectedRows.map((r) => toBackendStatus(r.status ?? r.contractStatus ?? "")),
                    );
                    const allDraft = statuses.size === 1 && statuses.has("draft");
                    const allPending = statuses.size === 1 && statuses.has("pending_approval");
                    const allApproved = statuses.size === 1 && statuses.has("approved");
                    const allSent = statuses.size === 1 && statuses.has("sent");
                    const allCancellable = Array.from(statuses).every(
                      (s) => !["completed", "cancelled", "rejected"].includes(String(s)),
                    );

                    return (
                      <DataTableFloatingBar
                        selectedCount={selectedRows.length}
                        onClear={clearSelection}
                      >
                        {allDraft && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              void handleBulkStatusChange(
                                selectedRows,
                                "pending_approval",
                                "Submitted for approval",
                                clearSelection,
                              )
                            }
                          >
                            Submit for Approval
                          </Button>
                        )}
                        {allPending && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                void handleBulkStatusChange(
                                  selectedRows,
                                  "approved",
                                  "Approved",
                                  clearSelection,
                                )
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                void handleBulkStatusChange(
                                  selectedRows,
                                  "rejected",
                                  "Rejected",
                                  clearSelection,
                                )
                              }
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {allApproved && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              void handleBulkStatusChange(
                                selectedRows,
                                "sent",
                                "Marked as sent",
                                clearSelection,
                              )
                            }
                          >
                            Mark as Sent
                          </Button>
                        )}
                        {allSent && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              void handleBulkStatusChange(
                                selectedRows,
                                "signed",
                                "Marked as signed",
                                clearSelection,
                              )
                            }
                          >
                            Mark as Signed
                          </Button>
                        )}
                        {allCancellable && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              void handleBulkStatusChange(
                                selectedRows,
                                "cancelled",
                                "Cancelled",
                                clearSelection,
                              )
                            }
                          >
                            Cancel
                          </Button>
                        )}
                      </DataTableFloatingBar>
                    );
                  }
                : undefined
            }
          />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
